import axios from 'axios';
import { MAIN_API_BASE_URL } from '../config/api';
import { SKIP_AUTH } from '../config/auth';

// Snwolley API endpoint from environment variables
const API_BASE_URL = MAIN_API_BASE_URL;

// API configuration loaded

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // Increased to 5 minutes for large file uploads
  headers: {
    'Content-Type': 'application/json',
    'X-Device-Platform': 'courtscribe',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('courtscribe_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Handle FormData requests
    if (config.data instanceof FormData) {
      // Remove Content-Type header for FormData requests to let browser set it automatically
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !SKIP_AUTH) {
      localStorage.removeItem('courtscribe_token');
      localStorage.removeItem('courtscribe_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/login', userData),
  changePassword: (payload) => api.post('/auth/change-password', payload),
  me: () => api.get('/auth/me'),
  verifyToken: () => Promise.resolve({ data: { success: true } }),
  resetPassword: (email) => api.post('/reset-password', { email }),
  confirmResetPassword: (token, newPassword) =>
    api.post('/confirm-reset', { token, newPassword }),
};

const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024;

const uploadResumeKey = (tenantId, file, caseReference) =>
  `courtscribe-upload:${tenantId}:${caseReference}:${file.name}:${file.size}`;

const payloadData = (resp) => resp?.data?.data || resp?.data || {};

async function uploadFileInChunks(tenantId, { file, caseReference, language }, onProgress) {
  const key = uploadResumeKey(tenantId, file, caseReference);
  let uploadId = null;
  let chunkSize = DEFAULT_CHUNK_SIZE;
  let missing = null;

  try {
    uploadId = sessionStorage.getItem(key);
  } catch {
    uploadId = null;
  }

  if (uploadId) {
    try {
      const status = payloadData(await api.get(`/tenants/${tenantId}/uploads/${uploadId}`));
      chunkSize = status.chunk_size || chunkSize;
      missing = status.missing_chunks || [];
      if (status.validation_status === 'valid' || status.validation_status === 'failed') {
        try { sessionStorage.removeItem(key); } catch { /* ignore */ }
        return { data: { success: true, data: status } };
      }
    } catch {
      uploadId = null;
    }
  }

  if (!uploadId) {
    const created = payloadData(await api.post(`/tenants/${tenantId}/uploads/init`, {
      case_reference: caseReference,
      language,
      file_name: file.name,
      file_size_bytes: file.size,
    }));
    uploadId = created.upload_id;
    chunkSize = created.chunk_size || chunkSize;
    missing = created.missing_chunks;
    try { sessionStorage.setItem(key, uploadId); } catch { /* ignore */ }
  }

  const expected = Math.max(1, Math.ceil(file.size / chunkSize));
  const indexes = (missing && missing.length >= 0)
    ? missing
    : Array.from({ length: expected }, (_, index) => index);
  const alreadySent = expected - indexes.length;
  let sent = alreadySent;

  for (const index of indexes) {
    const start = index * chunkSize;
    const blob = file.slice(start, Math.min(start + chunkSize, file.size));
    await api.put(
      `/tenants/${tenantId}/uploads/${uploadId}/chunks/${index}`,
      blob,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        transformRequest: [(data) => data],
        onUploadProgress: (progressEvent) => {
          const chunkPct = progressEvent.loaded / (progressEvent.total || blob.size || 1);
          const overall = Math.round(((sent + chunkPct) / expected) * 100);
          onProgress?.(Math.min(99, overall));
        },
      }
    );
    sent += 1;
    onProgress?.(Math.min(99, Math.round((sent / expected) * 100)));
  }

  try {
    const done = await api.post(`/tenants/${tenantId}/uploads/${uploadId}/complete`);
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    onProgress?.(100);
    return done;
  } catch (error) {
    if (error.response?.status === 409 && error.response?.data?.code === 'incomplete') {
      const stillMissing = error.response.data.missing_chunks || [];
      for (const index of stillMissing) {
        const start = index * chunkSize;
        const blob = file.slice(start, Math.min(start + chunkSize, file.size));
        await api.put(
          `/tenants/${tenantId}/uploads/${uploadId}/chunks/${index}`,
          blob,
          {
            headers: { 'Content-Type': 'application/octet-stream' },
            transformRequest: [(data) => data],
          }
        );
      }
      const done = await api.post(`/tenants/${tenantId}/uploads/${uploadId}/complete`);
      try { sessionStorage.removeItem(key); } catch { /* ignore */ }
      onProgress?.(100);
      return done;
    }
    throw error;
  }
}

// Live recording session lifecycle (tenant-scoped)
export const recordingAPI = {
  createSession: (tenantId, data) =>
    api.post(`/tenants/${tenantId}/recordings`, data),
  startSession: (tenantId, sessionId) =>
    api.post(`/tenants/${tenantId}/recordings/${sessionId}/start`),
  pauseSession: (tenantId, sessionId) =>
    api.post(`/tenants/${tenantId}/recordings/${sessionId}/pause`),
  resumeSession: (tenantId, sessionId) =>
    api.post(`/tenants/${tenantId}/recordings/${sessionId}/resume`),
  stopSession: (tenantId, sessionId, formData, onProgress = null) => {
    const config = {};
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        onProgress(percentCompleted);
      };
    }
    return api.post(
      `/tenants/${tenantId}/recordings/${sessionId}/stop`,
      formData,
      config
    );
  },
  getSession: (tenantId, sessionId) =>
    api.get(`/tenants/${tenantId}/recordings/${sessionId}`),
  listSessions: (tenantId, params = {}) =>
    api.get(`/tenants/${tenantId}/recordings`, { params }),
  uploadRecording: (tenantId, formData, onProgress = null) => {
    const config = {};
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        onProgress(percentCompleted);
      };
    }
    return api.post(`/tenants/${tenantId}/uploads`, formData, config);
  },
  initUpload: (tenantId, payload) =>
    api.post(`/tenants/${tenantId}/uploads/init`, payload),
  putUploadChunk: (tenantId, uploadId, chunkIndex, blob, onProgress = null) => {
    const config = {
      headers: { 'Content-Type': 'application/octet-stream' },
      transformRequest: [(data) => data],
    };
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        onProgress(percentCompleted);
      };
    }
    return api.put(
      `/tenants/${tenantId}/uploads/${uploadId}/chunks/${chunkIndex}`,
      blob,
      config
    );
  },
  completeUpload: (tenantId, uploadId) =>
    api.post(`/tenants/${tenantId}/uploads/${uploadId}/complete`),
  getUpload: (tenantId, uploadId) =>
    api.get(`/tenants/${tenantId}/uploads/${uploadId}`),
  uploadRecordingChunked: (tenantId, { file, caseReference, language }, onProgress = null) =>
    uploadFileInChunks(tenantId, { file, caseReference, language }, onProgress),
};

// Transcription endpoints
const transcriptionAPIImpl = {
  getTranscriptions: (params) => api.get('/transcriptions', { params }),
  getTranscription: (idOrKey, params = {}) => {
    return api.get(`/transcriptions/${idOrKey}`, { params }).catch(async (error) => {
      if (error.response?.status === 405 || error.response?.status === 404) {
        try {
          const listResp = await api.get('/transcriptions', { params: { page: 1, per_page: 1000, ...params } });
          const allTranscriptions = listResp.data?.data || listResp.data || [];
          const keyStr = String(idOrKey).toLowerCase();
          const transcription = allTranscriptions.find((t) => {
            const idMatch = String(t.id) === keyStr || String(t.transcription_id) === keyStr;
            const nameMatch = [t.title, t.filename, t.name, t.audio_filename]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase() === keyStr);
            return idMatch || nameMatch;
          });
          if (transcription) {
            return { data: { data: transcription } };
          }
          throw new Error('Transcription not found in fallback list');
        } catch (listErr) {
          throw listErr;
        }
      }
      throw error;
    });
  },
  createTranscription: (data) => api.post('/transcriptions', data),
  updateTranscription: (id, data) => api.put(`/transcriptions/${id}`, data),
  getAudioOffsets: (id) => api.get(`/transcriptions/${id}/audio/offsets`),
  getAudioCue: (id, seconds) => api.get(`/transcriptions/${id}/audio/cue`, { params: { t: seconds } }),
  deleteTranscription: (id) => api.delete(`/transcriptions/${id}`),
  uploadAudio: (formData, onProgress = null) => {
    const config = {
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    return api.post('/mobile/transcription/upload-audio', formData, config);
  },

  uploadAudioAlternative: (formData, onProgress = null) => {
    const config = {
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    return api.post('/transcriptions', formData, config);
  },

  startTranscription: (id) => api.post(`/transcriptions/${id}/start`),
  certifyTranscription: (id, data = {}) => api.post(`/transcriptions/${id}/certify`, data),
  updateSpeakerLabels: (id, data) => api.put(`/transcriptions/${id}/speaker-labels`, data),
  requestRegeneration: (id, data) =>
    api.post('/regeneration-requests', {
      source_transcript_id: id,
      ...data,
    }),
  getAvailableLanguages: (audioAssetId) =>
    api.get(`/audio-assets/${audioAssetId}/available-languages`),
  getLanguageVersions: (audioAssetId) =>
    api.get(`/audio-assets/${audioAssetId}/language-versions`),
  getTranscriptionStatus: (id) => api.get(`/transcriptions/${id}/status`),
  getTranscriptionSummary: (id) => api.get(`/transcriptions/${id}/summary`),
  exportTranscription: (id, format) =>
    api.get(`/transcriptions/${id}/export?format=${format}`, { responseType: 'blob' }),
};

export const transcriptionAPI = transcriptionAPIImpl;

// AI features endpoints
export const aiAPI = {
  summarizeText: (text) => api.post('/ai/summarize', { text }),
  extractKeyPoints: (text) => api.post('/ai/extract-key-points', { text }),
  generateCitations: (text) => api.post('/ai/generate-citations', { text }),
  proofreadText: (text) => api.post('/ai/proofread', { text }),
  legalResearch: (query) => api.post('/ai/legal-research', { query }),
};

// User management endpoints
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),
  changePassword: (data) => api.post('/user/change-password', data),
  getUsage: () => api.get('/user/usage'),
  getBilling: () => api.get('/user/billing'),
};

// Platform configuration
export const configAPI = {
  getLanguages: () => api.get('/config/languages'),
};

// Court/Judicial endpoints
const courtAPIImpl = {
  getCourtCases: (params) => api.get('/court/cases', { params }),
  getCourtCase: (id) => api.get(`/court/cases/${encodeURIComponent(id)}`),
  createCourtCase: (data) => api.post('/court/cases', data),
  updateCourtCase: (id, data) => api.put(`/court/cases/${encodeURIComponent(id)}`, data),
  getCaseParticipants: (caseId) =>
    api.get(`/court/cases/${encodeURIComponent(caseId)}/participants`),
  addCaseParticipant: (caseId, data) =>
    api.post(`/court/cases/${encodeURIComponent(caseId)}/participants`, data),
};

export const courtAPI = courtAPIImpl;

export default api;